import React, { Fragment, useEffect, useRef, useState } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { map, orderBy, uniqBy } from "lodash";
import { Grid, GridCellProps, GridColumn } from "@progress/kendo-react-grid";
import { FormText } from "reactstrap";
import { useAppDispatch } from "../Core/store.hooks";
import { removeSeries } from "./machineHealthSlice";
import { Button } from "@progress/kendo-react-buttons";
import { useSelector } from "react-redux";
import { getOptionsSelector } from "../Common/Slices/optionSlice";
import { calculatorIcon } from "@progress/kendo-svg-icons";

interface MachineHealthChartProps{
    id:string;
    chartData:any[];
    totalData:any[];
    updateLegendSeries:any;
}

interface CustomCellProps extends GridCellProps {
    onClickProp: any;
    onMouseOverProp?: Function;
    onMouseLeaveProp?: Function;
  }

const chartColors = [
    am4core.color("#ff1500"),
    am4core.color("#3264a8"),
    am4core.color("#52a832"),
    am4core.color("#f78e05"),
    am4core.color("#7b00ff"),
    am4core.color("#383838"),
    am4core.color("#ff61e2"),
    am4core.color("#00eeff"),
    am4core.color("gray"),
    am4core.color("#ff5100"),
    am4core.color("#0080ff"),
    am4core.color("#00592a"),
    am4core.color("#593900"),
    am4core.color("#002e4f"),
];


function am4themes_myTheme(target: any) {
    if (target instanceof am4core.ColorSet) {
        target.list = chartColors;
    }
}
am4core.useTheme(am4themes_myTheme);
//const chartStyles = { height: "100%", zIndex: 31 };

const MachineHealthChart = (props:MachineHealthChartProps) =>{
    const drillDown = useSelector(getOptionsSelector).machineHealthDrillDown 
    const groupSize = useSelector(getOptionsSelector).machineHealthGroupSize

    const dispatch = useAppDispatch();

    let chart = useRef(null)
    let dateAxis: any = useRef(null);
    am4core.options.autoDispose = true;

    useEffect(()=>{
        
        if(props.chartData != null){
            chart.current = am4core.create("machine-health-chart", am4charts.XYChart);
            createChart(props.chartData)
        }
        return function cleanup() {
            if (chart.current) {
                chart.current.dispose();
            }
        };
     },[props.chartData,groupSize, drillDown])
    
    const getTotalData = (seriesID:string) =>{
        const seriesTotal = props.totalData.find((obj)=>obj.seriesID === seriesID)
        
        return seriesTotal
    }

    const intervalEnum = {
        None: 0,
        Day: 1,
        Week: 2,
        Month: 3,
        Year: 4,
    }

    const createChart = (data) =>{
        //add data
        chart.current.data = orderBy(data,["date"],"asc")
        // Create axes
        dateAxis.current = chart.current.xAxes.push(new am4charts.DateAxis());
        //dateAxis.renderer.minGridDistance = 50;
        //dateAxis.current.tooltipDateFormat = "yyyy-MM-dd";
        dateAxis.current.groupData = true;
        dateAxis.current.baseInterval = { timeUnit: "millisecond", count: 1 };
        dateAxis.current.groupIntervals.setAll([
            { timeUnit: "day", count: 1 },
            { timeUnit: "week", count: 1 },
            { timeUnit: "month", count: 1 },
            { timeUnit: "year", count: 1 },
          ]);

          dateAxis.current.events.on("startchanged", dateAxisChanged);
          dateAxis.current.events.on("endchanged", dateAxisChanged);
          function dateAxisChanged(ev:any) {
            const start:any = new Date(ev.target.minZoomed);
            const end:any = new Date(ev.target.maxZoomed);
            const totalDays = Math.ceil((end-start) / (1000 * 3600 * 24));
            
            if(drillDown && groupSize !== "None"){
                if(totalDays < 7){
                    dateAxis.current.groupInterval = { timeUnit: "millisecond", count: 1 };
                }
                if(totalDays > 7 && totalDays < 50 && intervalEnum[groupSize] >= 1){
                    dateAxis.current.groupInterval =  { timeUnit: "day", count: 1 };
                }
                if(totalDays > 50 && totalDays < 200 && intervalEnum[groupSize] >= 2){
                    dateAxis.current.groupInterval =  { timeUnit: "week", count: 1 };
                }
                if(totalDays > 200 && totalDays < 700 && intervalEnum[groupSize] >= 3){
                    dateAxis.current.groupInterval =  { timeUnit: "month", count: 1 };
                }
                if(totalDays > 700 && intervalEnum[groupSize] >= 4){
                    dateAxis.current.groupInterval =  { timeUnit: "year", count: 1 };
                }
                if(ev.target.start === 0 && ev.target.end === 1){
                    dateAxis.current.groupInterval = {timeUnit:groupSize.toLocaleLowerCase(),count:1}
                }
            }
          }

        if(groupSize==="None"){
            dateAxis.current.groupInterval = { timeUnit:"millisecond",count: 1}
        }
        if(groupSize === "Day"){
            dateAxis.current.groupInterval =  { timeUnit: "day", count: 1 };
        }
        if(groupSize === "Week"){
            dateAxis.current.groupInterval =  { timeUnit: "week", count: 1 };
        }
        if(groupSize === "Month"){
            dateAxis.current.groupInterval =  { timeUnit: "month", count: 1 };
        }
        if(groupSize === "Year"){
            dateAxis.current.groupInterval =  { timeUnit: "year", count: 1 };
        }

        var valueAxis = chart.current.yAxes.push(new am4charts.ValueAxis());
        valueAxis.title.text = "Interrupts per running hour"

        //valueAxis.tooltip.disabled = true;
        // Add scrollbar
        // chart.current.scrollbarX = new am4charts.XYChartScrollbar();
        // chart.current.scrollbarX.minHeight = 30;

        // Add cursor
        chart.current.cursor = new am4charts.XYCursor();
        chart.current.cursor.xAxis = dateAxis.current;
        chart.current.cursor.behavior = "panX";
        chart.current.mouseWheelBehavior = "zoomX";

        const uniqueSeriesIDs = uniqBy(data,"seriesID")

        let tempSeriesData:any[] = []

        uniqueSeriesIDs.forEach((obj:any)=>{
            //
            createSeries(obj.seriesID,obj.seriesID)
        })
        props.updateLegendSeries(tempSeriesData)

        function createSeries(field:string, name:string){
            // Create series
            let series = chart.current.series.push(new am4charts.LineSeries());
            series.name = name;
            //series.dataFields.dateX = "timestamp" + props.data[i].name;
            
            series.dataFields.dateX = "date" + name;
            series.dataFields.valueY = field;
            //series.groupFields.valueY = "average";
            series.strokeWidth = 2;
            //series.minBulletDistance = 10;
            //series.tooltip.pointerOrientation = "vertical";
            // series.tooltip.background.cornerRadius = 20;
            // series.tooltip.background.fillOpacity = 0.5;
            series.tooltip.label.padding(12,12,12,12)
            var bullet = series.bullets.push(
                new am4charts.CircleBullet()
            );
            series.tooltipText = "{name}:  Interrupts/ running hr : {valueY.value} [/]\n Interrupts: {interruptCount} | Running hours: {runningDuration}";
            bullet.circle.strokeWidth = 1;
            bullet.circle.radius = 3;

            let currentColor;
            if(name === "All Devices"){
                currentColor = chart.current.colors.getIndex(0);
            }else{
                currentColor = chart.current.colors.next();
            }

            series.stroke = currentColor;
            series.fill = currentColor;
            series.tooltipColor = currentColor

            const seriesTotals = getTotalData(field);

            tempSeriesData.push({
                seriesID:field, 
                color:currentColor, 
                series:series, 
                bullet:bullet,
                interruptCount: seriesTotals.interruptCount,
                runningDuration: seriesTotals.runningDuration,
                interruptsPerRunningHour: seriesTotals.interruptsPerRunningHour
            })
        
            series.adapter.add("groupDataItem", function(val:any) {
                //
                let durationTotal=0;
                let countTotal=0;
                for(let i = 0; i < val.dataItem.groupDataItems.length; i++) {
                    //
                    durationTotal = durationTotal+ val.dataItem.groupDataItems[i]._dataContext.runningDuration
                    countTotal = countTotal + val.dataItem.groupDataItems[i]._dataContext.interruptCount
                }
                if(durationTotal === 0){
                    val.value = 0;
                }else{
                    val.value = countTotal/durationTotal
                }
                val.dataItem.runningDuration= durationTotal
                val.dataItem.interruptCount = countTotal
                //
                return val;
            });
        }
    }

    return (
    <Fragment>
        <div style={{"width":"100%", "height":"100%"}}>
            <div id="machine-health-chart" style={{ width: "100%", height: "100%"}}></div>
        </div>
    </Fragment>
    )
}

export default MachineHealthChart;